'use client';
import React, { useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { extend } from '@react-three/fiber';
import { MathUtils, Vector3, Color } from 'three';
import * as THREE from 'three';

extend({ PlaneGeometry: THREE.PlaneGeometry });

const vertexShader = `
uniform float u_time;
uniform vec2 u_resolution;

varying vec2 vUv;
varying float vNoise;

// Simple noise function
float noise(vec2 st) {
    return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
}

// Smooth noise
float smoothNoise(vec2 st) {
    vec2 i = floor(st);
    vec2 f = fract(st);
    
    float a = noise(i);
    float b = noise(i + vec2(1.0, 0.0));
    float c = noise(i + vec2(0.0, 1.0));
    float d = noise(i + vec2(1.0, 1.0));
    
    vec2 u = f * f * (3.0 - 2.0 * f);
    
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

void main() {
    vUv = uv;
    
    // Create flowing movement
    vec2 pos = uv + vec2(u_time * 0.1, u_time * 0.05);
    vNoise = smoothNoise(pos * 3.0) * 0.5 + smoothNoise(pos * 6.0) * 0.25;
    
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = `
uniform float u_time;
uniform vec2 u_resolution;
uniform vec3 u_color1;
uniform vec3 u_color2;
uniform vec3 u_color3;

varying vec2 vUv;
varying float vNoise;

void main() {
    vec2 uv = vUv;
    
    // Create flowing gradient
    float gradient1 = sin(uv.x * 2.0 + u_time * 0.3) * 0.5 + 0.5;
    float gradient2 = cos(uv.y * 3.0 + u_time * 0.2) * 0.5 + 0.5;
    float gradient3 = sin((uv.x + uv.y) * 1.5 + u_time * 0.4) * 0.5 + 0.5;
    
    // Mix the colors based on gradients and noise
    vec3 color = mix(u_color1, u_color2, gradient1 + vNoise * 0.3);
    color = mix(color, u_color3, gradient2 * gradient3 * 0.6);
    
    // Add subtle brightness variation
    color += vNoise * 0.1;
    
    gl_FragColor = vec4(color, 1.0);
}
`;

interface LoginLeftMeshGradientProps {
  className?: string;
}

const AnimatedGradient: React.FC = () => {
  const mesh = useRef<THREE.Mesh>(null);

  const uniforms = useMemo(
    () => ({
      u_time: { value: 0 },
      u_resolution: { value: new Vector3(window.innerWidth, window.innerHeight) },
      u_color1: { value: new Color('#3b82f6') }, // Blue
      u_color2: { value: new Color('#fb8ff6') }, // Pink
      u_color3: { value: new Color('#a21caf') }, // Purple
    }),
    [],
  );

  useFrame((state) => {
    const { clock } = state;

    if (mesh.current) {
      const material = mesh.current.material as THREE.ShaderMaterial;
      material.uniforms.u_time.value = clock.getElapsedTime();
    }
  });

  return (
    <mesh ref={mesh}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial vertexShader={vertexShader} fragmentShader={fragmentShader} uniforms={uniforms} />
    </mesh>
  );
};

const LoginLeftMeshGradient: React.FC<LoginLeftMeshGradientProps> = ({ className = '' }) => {
  return (
    <div className={`absolute inset-0 ${className}`}>
      <Canvas camera={{ position: [0, 0, 1], fov: 75 }} style={{ width: '100%', height: '100%' }}>
        <AnimatedGradient />
      </Canvas>
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/60"></div>
    </div>
  );
};

export default LoginLeftMeshGradient;
